import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  HostListener,
  NgModule,
  OnInit,
  Signal,
  signal,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { Navigate } from '@ngxs/router-plugin';
import { NgxsModule, select } from '@ngxs/store';
import {
  AnimateModule,
  FormErrorHandlerService,
  InputValidatorDirective,
  InvalidControlScrollDirective,
  ValidateMessageComponent,
} from '@pro-connect/shared';
import { NgxMaskDirective } from 'ngx-mask';
import { ToastrService } from 'ngx-toastr';
import { of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { BasicPage, WwwRouteSlug } from '../../../../models';
import { EventTicketState, EventTicketStateGetDetails, EventTicketStateRegister } from './event-ticket.state';
import { format } from 'date-fns';

export type EventTicketFormType = {
  firstname: FormControl<string | null>;
  lastname: FormControl<string | null>;
  email: FormControl<string | null>;
  mobile: FormControl<string | null>;
  agreementTraining: FormControl<boolean | null>;
  agreementSms: FormControl<boolean | null>;
  agreementEmail: FormControl<boolean | null>;
  agreementPhone: FormControl<boolean | null>;
  agreementWhatsApp: FormControl<boolean | null>;
};

@NgModule({
  imports: [NgxsModule.forFeature([EventTicketState])],
})
class EventTicketStateLazyModule {}

@Component({
  templateUrl: './event-ticket.component.html',
  styleUrls: ['./event-ticket.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    AnimateModule,
    ReactiveFormsModule,
    NgxsFormPluginModule,
    ValidateMessageComponent,
    InputValidatorDirective,
    EventTicketStateLazyModule,
    InvalidControlScrollDirective,
    NgxMaskDirective,
    FormsModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class EventTicketComponent extends BasicPage implements OnInit {
  public readonly steps = signal([false, false, false]);
  public readonly currentStep = signal<1 | 2 | 3>(1);
  public readonly loading = select(EventTicketState.loading);
  public readonly details = select(EventTicketState.eventDetails);
  public readonly successFullyRegistered = signal(false);
  public readonly eventInactive = signal(false);

  public readonly WwwRouteSlug = WwwRouteSlug;
  public readonly formGroup: FormGroup<EventTicketFormType>;
  public code: string | null = null;
  public clouseExpanded: WritableSignal<boolean> = signal(false);
  public currentDate: Signal<string> = signal(format(new Date(), 'dd.MM.yyyy'));

  constructor(
    private readonly _toastrService: ToastrService,
    private readonly _formErrorHandlerService: FormErrorHandlerService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _formBuilder: FormBuilder,
  ) {
    super();
    // this._code = this._activatedRoute.snapshot.params['code'];
    this.formGroup = this._formBuilder.group({
      firstname: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(255)]),
      lastname: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(255)]),
      mobile: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(255)]),
      email: new FormControl<string | null>(null, [Validators.required, Validators.email, Validators.maxLength(255)]),
      agreementTraining: new FormControl<boolean | null>(false, [Validators.requiredTrue]),
      agreementSms: new FormControl<boolean | null>(false, []),
      agreementEmail: new FormControl<boolean | null>(false, []),
      agreementPhone: new FormControl<boolean | null>(false, []),
      agreementWhatsApp: new FormControl<boolean | null>(false, []),
    });

    effect(() => {
      if (this.details()) {
        // if (this.details()?.firstname) {
        //   this.f.firstname.setValue(this.details()?.firstname || '');
        //   this.f.firstname.disable();
        // }
        // if (this.details()?.lastname) {
        //   this.f.lastname.setValue(this.details()?.lastname || '');
        //   this.f.lastname.disable();
        // }
        // this.f.disable();
      }
    });
  }

  get f() {
    return this.formGroup.controls;
  }

  @HostListener('document:keydown.enter', ['$event'])
  public onEnterKeydownHandler(event: KeyboardEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.onRegister();
  }

  public ngOnInit(): void {}

  public onGetDetails(): void {
    if (this.code) {
      this._getDetails();
    } else {
      this._toastrService.error('Wprowadź kod biletu!');
    }
  }

  public onRegister(): void {
    if (!this.code) {
      throw new Error('Code is required');
    }
    this.formGroup.markAllAsTouched();

    if (!this.formGroup.valid) {
      return;
    } else {
      this._store
        .dispatch(
          new EventTicketStateRegister({
            code: String(this.code),
          }),
        )
        .pipe(
          takeUntilDestroyed(this._destroyRef),
          catchError((error) => {
            if (error === 'timeout') {
              this._toastrService.error('Błąd przekroczenia limitu czasu żądania', 'Błąd');
            }
            this._formErrorHandlerService.catchFormError(error, this.formGroup);
            if (error?.error?.code === 400) {
              const children = error?.error?.errors?.children;

              for (const key in children) {
                if (children[key]?.errors?.length) {
                  this._toastrService.error(children[key]?.errors[0], 'Błąd');
                }
              }
            }

            if (error?.error?.code === 401) {
              this._toastrService.error(error.error.message);
            }
            this.eventInactive.set(true);
            return throwError(() => error);
          }),
          tap(() => {
            this._toastrService.success('Bilet zatwierdzony. Zapraszamy do udziału w wydarzeniu!', 'Sukces');
            this.successFullyRegistered.set(true);
          }),
        )
        .subscribe();
    }
  }

  private _getDetails(): void {
    if (!this.code) {
      this._toastrService.error('Niepoprawny kod biletu.', 'Błąd');
      this._store.dispatch(new Navigate([`/${WwwRouteSlug.NOT_FOUND_PAGE}`]));
      return;
    }

    this._store
      .dispatch(new EventTicketStateGetDetails({ code: String(this.code) }))
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        catchError((error) => {
          if (error === 'timeout') {
            this._toastrService.error('Błąd przekroczenia limitu czasu żądania.', 'Błąd');
            return of(error);
          }
          if (error?.status === 404) {
            this._toastrService.error('Twój link do rejestracji wygasł.');
            this.eventInactive.set(true);
            return of(null);
          }
          this._toastrService.error('Coś poszło nie tak.', 'Błąd');
          return throwError(() => error);
        }),
      )
      .subscribe();
  }
}
